import React, { useEffect } from 'react';
import { useStore } from 'effector-react';
import produce from 'immer';
import last from 'ramda/es/last';
import { nanoid } from 'nanoid';
import { flagToBool, toFlag } from 'pano-utils';
import { PUBLIC_DOMAIN } from '@constants';
import type { BooleanFlag, HSIconPolygon, PolygonHotspotPoint } from '@types';
import { Validator } from '@components/Validator';
import colorStringToHexAndAlpha from '@utils/colorStringToHexAndAlpha';
import Button from '@components/Button';
import {
  $editingHotspot,
  $editingHotspotApi,
} from '@stores/editingHotspotStores';
import { MainPlayer } from '@stores/krpPlayerStores';
import FeatureToggle from '@components/FeatureToggle';
import {
  onHotspotPointClick,
  onHotspotPointMoveEnd,
  onPlayerClick,
  onPolygonButtonClick,
  PM_ID_DEL,
  PM_ID_DONE,
  PM_ID_EDIT,
} from '../../../../hotspot';
import PolygonColorSetting from './PolygonColorSetting';

const ICON_EDIT = `${PUBLIC_DOMAIN}/imgs/polygon-edit.fbc85a6a5d7bc4c100db9eed3c9b00b7.png`;
const ICON_DONE = `${PUBLIC_DOMAIN}/imgs/polygon-done.6cefbb9ded9f45ac8d31254883bdf334.png`;
const ICON_DEL = `${PUBLIC_DOMAIN}/imgs/polygon-del.ef32932089415e315cf93d25a3356bea.png`;

const params = {
  dragable: false,
  scaleX: 0.5,
  scaleY: 0.5,
  zOrder: 99,
};

const pointsData = (points: PolygonHotspotPoint[]) => {
  return points.map((x) => ({ ath: x.ath, atv: x.atv }));
};

function IconSettingPolygon() {
  const { hotspot } = useStore($editingHotspot)!;
  const player = MainPlayer.get();
  const icon = hotspot.icon as HSIconPolygon;

  const isEditingMode = () => {
    return player?.getHotspotById(hotspot.id).dragable || false;
  };

  const getKrpPoints = () => {
    return player?.getHotspotById(hotspot.id).points || [];
  };

  const addEditingIcon = ({ ath, atv }: { ath: number; atv: number }) => {
    player?.addImageHotspot({
      id: PM_ID_DONE,
      ath,
      atv,
      ox: -25,
      oy: -30,
      urls: [ICON_DONE],
      ...params,
    });
    player?.addImageHotspot({
      id: PM_ID_DEL,
      ath,
      atv,
      ox: 25,
      oy: -30,
      urls: [ICON_DEL],
      ...params,
    });
  };

  const addEditIcon = ({ ath, atv }: { ath: number; atv: number }) => {
    player?.addImageHotspot({
      id: PM_ID_EDIT,
      ath,
      atv,
      oy: -30,
      urls: [ICON_EDIT],
      ...params,
    });
  };

  const addPoint = ({ ath, atv }: { ath: number; atv: number }) => {
    const newPoints = produce(getKrpPoints(), (draft) => {
      const index = draft.findIndex((x) => !!x.selected);
      draft.splice(index + 1, 0, { ath, atv, selected: true });
      if (index >= 0) {
        draft[index].selected = false;
      }
    });
    player?.updatePolygonHotspot({ id: hotspot.id, points: newPoints });
    $editingHotspotApi.update((draft) => {
      (draft.icon as HSIconPolygon).points = pointsData(newPoints);
    });
    addEditingIcon({ ath, atv });
  };

  useEffect(() => {
    if (icon.points.length) {
      const lastPoint = last(icon.points)!;
      addEditIcon({ ath: lastPoint.ath, atv: lastPoint.atv });
    } else {
      player?.updatePolygonHotspot({
        id: hotspot.id,
        dragable: hotspot.locked !== 1,
      });
    }

    const unwatch0 = onPlayerClick.watch(({ ath, atv }) => {
      if (isEditingMode()) {
        addPoint({ ath, atv });
      }
    });
    const unwatch1 = onPolygonButtonClick.watch((type) => {
      if (type === 'done') {
        player?.removeHotspotById(PM_ID_DEL);
        player?.removeHotspotById(PM_ID_DONE);
        player?.updatePolygonHotspot({
          id: hotspot.id,
          dragable: hotspot.locked !== 1,
        });
        const lastPoint = last(getKrpPoints());
        if (lastPoint) {
          addEditIcon({ ath: lastPoint.ath, atv: lastPoint.atv });
          if (!player?.getHotspotById(hotspot.id).titleHotspotId) {
            const data = $editingHotspot.getState()?.hotspot!;
            const { titleSetting } = data;
            const textId = `pht_${nanoid()}`;
            const fc = colorStringToHexAndAlpha(titleSetting.font?.fontColor);
            const bgc = colorStringToHexAndAlpha(
              titleSetting.background?.bgColor
            );
            player?.addTextHotspot({
              id: textId,
              text: data.title,
              ath: lastPoint.ath,
              atv: lastPoint.atv,
              padding: '5 10',
              align: titleSetting.align,
              zOrder: player?.getHotspotById(data.id).zOrder,
              dragable: hotspot.locked !== 1,
              visible: !!data.showTitle,
              font: {
                fontSize: titleSetting.font?.fontSize,
                fontColor: fc.hex,
              },
              background: {
                bgColor: bgc.hex,
                bgAlpha: bgc.alpha,
                bgRoundedge: 5,
                borderWidth: 0,
              },
            });
            player?.getHotspotById(textId)?.addExtendData('polygonId', data.id);
            player?.updatePolygonHotspot({
              id: data.id,
              titleHotspotId: textId,
            });
          }
          $editingHotspotApi.update((draft) => {
            (draft.icon as HSIconPolygon).ath = lastPoint.ath;
            (draft.icon as HSIconPolygon).atv = lastPoint.atv;
          });
        }
      } else if (type === 'edit') {
        player?.removeHotspotById(PM_ID_EDIT);
        player?.updatePolygonHotspot({
          id: hotspot.id,
          dragable: hotspot.locked !== 1,
        });
        const lastPoint = last(getKrpPoints());
        if (lastPoint) {
          addEditingIcon({ ath: lastPoint.ath, atv: lastPoint.atv });
        }
      } else if (type === 'del') {
        const newPoints = produce(getKrpPoints(), (draft) => {
          const index = draft.findIndex((x) => !!x.selected);
          if (index < 0) return;
          draft.splice(index, 1);
          if (draft[index - 1]) {
            draft[index - 1].selected = true;
          } else if (draft.length) {
            draft[draft.length - 1].selected = true;
          }
        });
        player?.updatePolygonHotspot({
          id: hotspot.id,
          points: newPoints,
        });
        const selected = newPoints.find((x) => !!x.selected);
        if (selected) {
          addEditingIcon({ ath: selected.ath, atv: selected.atv });
        } else {
          player?.removeHotspotById(PM_ID_DEL);
          player?.removeHotspotById(PM_ID_DONE);
        }
        $editingHotspotApi.update((draft) => {
          (draft.icon as HSIconPolygon).points = pointsData(newPoints);
        });
      }
    });

    const unwatch2 = onHotspotPointClick.watch(({ id, index }) => {
      if (id !== hotspot.id) return;
      const point = getKrpPoints()[index];
      if (!point) return;
      const newPoints = produce(getKrpPoints(), (draft) => {
        const selectedPoint = draft.find((x) => !!x.selected);
        if (selectedPoint) {
          selectedPoint.selected = false;
        }
        draft[index].selected = true;
      });
      player?.updatePolygonHotspot({
        id: hotspot.id,
        points: newPoints,
      });
      addEditingIcon({ ath: point.ath, atv: point.atv });
    });

    const unwatch3 = onHotspotPointMoveEnd.watch(({ id, index }) => {
      if (id !== hotspot.id) return;
      const point = getKrpPoints()[index];
      if (!point) return;
      $editingHotspotApi.update((draft) => {
        (draft.icon as HSIconPolygon).points[index] = {
          ath: point.ath,
          atv: point.atv,
        };
      });
      const selectedPoint = getKrpPoints().find((x) => !!x.selected);
      if (selectedPoint) {
        addEditingIcon({ ath: selectedPoint.ath, atv: selectedPoint.atv });
      }
    });

    return () => {
      unwatch0();
      unwatch1();
      unwatch2();
      unwatch3();
      player?.removeHotspotById(PM_ID_DEL);
      player?.removeHotspotById(PM_ID_DONE);
      player?.removeHotspotById(PM_ID_EDIT);
      player?.updatePolygonHotspot({ id: hotspot.id, dragable: false });
    };
  }, []);

  const handleDelete = () => {
    player?.removeHotspotById(PM_ID_DEL);
    player?.removeHotspotById(PM_ID_DONE);
    player?.removeHotspotById(PM_ID_EDIT);
    $editingHotspotApi.update((draft) => {
      (draft.icon as HSIconPolygon).points = [];
    });
    player?.updatePolygonHotspot({
      id: hotspot.id,
      dragable: hotspot.locked !== 1,
      points: [],
    });
  };

  const handleBlinkToggle = (val: BooleanFlag) => {
    $editingHotspotApi.update((draft) => {
      (draft.icon as HSIconPolygon).blink = val;
    });
    player?.updatePolygonHotspot({
      id: hotspot.id,
      blink: flagToBool(val),
    });
  };

  return (
    <Validator
      value={icon.points}
      validFn={(value) => value.length >= 3}
      warningMessage="请在全景中绘制一个多边形以继续。"
    >
      {({ value }) => (
        <>
          <PolygonColorSetting />
          <FeatureToggle
            title="默认/移入状态交替变化"
            className="mt-20"
            isOn={!!((hotspot.icon as HSIconPolygon).blink ?? false)}
            onToggle={(isOn) => {
              handleBlinkToggle(toFlag(isOn));
            }}
          />
          <Button
            className="mt-20 w-full"
            disabled={!value.length}
            onClick={handleDelete}
          >
            删除多边形
          </Button>
        </>
      )}
    </Validator>
  );
}

export default IconSettingPolygon;
